import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, vModelText as _vModelText, withKeys as _withKeys, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "bg-chatbot" }
const _hoisted_2 = { class: "container" }
const _hoisted_3 = { class: "mb-5" }
const _hoisted_4 = {
  key: 0,
  class: "flex align-items-center justify-content-between"
}
const _hoisted_5 = { class: "flex align-items-center" }
const _hoisted_6 = ["src"]
const _hoisted_7 = {
  key: 1,
  class: "size-2xl rounded-100 mr-4 bg-grey-200 flex align-items-center justify-content-center"
}
const _hoisted_8 = { class: "text-lg" }
const _hoisted_9 = { class: "font-semibold mb-0" }
const _hoisted_10 = { class: "text-grey-500 mb-0" }
const _hoisted_11 = { class: "form-field-floating sm" }
const _hoisted_12 = ["value"]
const _hoisted_13 = { class: "widget-chatbot" }
const _hoisted_14 = {
  key: 0,
  class: "widget-chatbot-header"
}
const _hoisted_15 = ["src"]
const _hoisted_16 = {
  key: 0,
  class: "text-center my-5"
}
const _hoisted_17 = {
  key: 1,
  class: "text-center my-5 text-grey-500"
}
const _hoisted_18 = { class: "widget-chatbot-send-message" }
const _hoisted_19 = { class: "widget-chatbot-input-set" }
const _hoisted_20 = ["disabled"]
const _hoisted_21 = ["disabled"]
const _hoisted_22 = {
  key: 0,
  class: "text-red text-sm mt-4 text-center"
}

import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import chatService, { type ChatConversation, type ChatMessage } from '@/services/chatService';
    import { formatDate } from '../../utils';
    import { useNotificationStore } from '@/stores/notificationStore';
    import { onEvent, offEvent, joinRoom, leaveRoom, getSocket, isSocketConnected } from '@/config/socket';
    
    
export default /*@__PURE__*/_defineComponent({
  __name: 'ServiceClientDetailView',
  setup(__props) {

    const route = useRoute();
    const router = useRouter();
    const notificationStore = useNotificationStore();
    const conversationId = ref<number | null>(null);
    const conversation = ref<ChatConversation | null>(null);
    const messages = ref<ChatMessage[]>([]);
    const newMessage = ref('');
    const isSending = ref(false);
    const loading = ref(false);
    const errorMessage = ref('');
    const messagesContainer = ref<HTMLElement | null>(null);
    let fallbackInterval: number | null = null;
    let connectionCheckInterval: number | null = null;
    let socketConnected = ref(true); // On assume que Socket.IO est connecté par défaut
    const recentlySentMessageIds = new Set<number>(); // Garder une trace des messages récemment envoyés

    // Callback pour les nouveaux messages reçus via Socket.IO
    const handleNewMessage = (data: any) => {
        console.log('[ChatDetail] Nouveau message reçu via Socket:', data);
        if (data.conversation_id === conversationId.value || data.message?.conversationId === conversationId.value) {
            // Ajouter le message s'il n'existe pas déjà
            const messageId = data.message?.id || data.id;
            
            // Vérifier si le message existe déjà dans la liste
            const messageExists = messages.value.some(m => m.id === messageId);
            
            // Vérifier si c'est un message qu'on vient d'envoyer (pour éviter les doublons)
            const isRecentlySent = recentlySentMessageIds.has(messageId);
            
            if (!messageExists && !isRecentlySent) {
                const newMessage: ChatMessage = data.message || {
                    id: messageId,
                    conversationId: conversationId.value!,
                    texte: data.texte || data.message?.texte || '',
                    date: data.date || data.message?.date || new Date().toISOString(),
                    statut: data.statut || data.message?.statut || 'sent',
                    qui: data.qui || data.message?.qui || (data.user_id ? 'user' : 'bot'),
                    metadata: data.metadata || data.message?.metadata || null,
                    agent: data.agent || data.message?.agent || null,
                };
                messages.value.push(newMessage);
                nextTick(() => scrollToBottom());
                
                // Si c'est un message utilisateur, le marquer comme lu immédiatement
                if (newMessage.qui === 'user') {
                    markMessagesAsRead();
                }
            } else if (isRecentlySent) {
                // Si c'est un message qu'on vient d'envoyer, on l'ignore car il est déjà dans la liste
                console.log('[ChatDetail] Message ignoré (déjà envoyé):', messageId);
                // Retirer de la liste des messages récents après un court délai
                setTimeout(() => {
                    recentlySentMessageIds.delete(messageId);
                }, 2000);
            }
        }
    };

    // Vérifier la connexion Socket.IO et activer le fallback si nécessaire
    function checkSocketConnection() {
        socketConnected.value = isSocketConnected();
        
        // Si Socket.IO n'est pas connecté, activer le polling de fallback
        if (!socketConnected.value && !fallbackInterval) {
            console.warn('[ChatDetail] Socket.IO non connecté, activation du polling de fallback');
            fallbackInterval = window.setInterval(async () => {
                if (conversationId.value) {
                    await loadMessages();
                }
            }, 10000); // Polling toutes les 10 secondes en fallback
        } else if (socketConnected.value && fallbackInterval) {
            // Si Socket.IO se reconnecte, désactiver le polling
            console.log('[ChatDetail] Socket.IO reconnecté, désactivation du polling');
            if (fallbackInterval) {
                clearInterval(fallbackInterval);
                fallbackInterval = null;
            }
        }
    }

    // Marquer les messages comme lus
    async function markMessagesAsRead() {
        if (conversationId.value) {
            await notificationStore.markConversationRead(conversationId.value);
        }
    }

    onMounted(async () => {
        const id = parseInt(route.params.id as string);
        if (isNaN(id)) {
            router.push('/app/service-client');
            return;
        }
        conversationId.value = id;
        await loadConversation();
        await loadMessages();
        
        // Marquer les messages comme lus à l'ouverture
        await markMessagesAsRead();
        
        // Vérifier la connexion Socket.IO
        checkSocketConnection();
        
        // Rejoindre le room de la conversation pour les mises à jour en temps réel
        joinRoom(`conversation.${id}`);
        
        // Écouter les nouveaux messages via Socket.IO
        onEvent('new.message', handleNewMessage);
        
        // Vérifier périodiquement la connexion Socket.IO (toutes les 30 secondes)
        connectionCheckInterval = window.setInterval(() => {
            checkSocketConnection();
        }, 30000);
    });

    onUnmounted(() => {
        // Désactiver le polling de fallback s'il est actif
        if (fallbackInterval) {
            clearInterval(fallbackInterval);
            fallbackInterval = null;
        }
        // Désactiver la vérification de connexion
        if (connectionCheckInterval) {
            clearInterval(connectionCheckInterval);
            connectionCheckInterval = null;
        }
        // Quitter le room et nettoyer les listeners
        if (conversationId.value) {
            leaveRoom(`conversation.${conversationId.value}`);
        }
        offEvent('new.message', handleNewMessage);
    });

    async function loadConversation() {
        try {
            loading.value = true;
            errorMessage.value = '';
            if (!conversationId.value) return;
            conversation.value = await chatService.getConversation(conversationId.value);
        } catch (error: any) {
            errorMessage.value = error.message;
        } finally {
            loading.value = false;
        }
    }

    async function loadMessages() {
        try {
            if (!conversationId.value) return;
            const currentMessagesCount = messages.value.length;
            const newMessages = await chatService.getMessages(conversationId.value);
            
            // Ne mettre à jour que si de nouveaux messages sont arrivés
            if (newMessages.length !== currentMessagesCount) {
                messages.value = newMessages;
                await nextTick();
                scrollToBottom();
            }
        } catch (error: any) {
            errorMessage.value = error.message;
        }
    }


    async function sendMessage() {
        if (!newMessage.value.trim() || !conversationId.value || isSending.value) return;

        try {
            isSending.value = true;
            errorMessage.value = '';
            
            const message = await chatService.sendMessage(conversationId.value, newMessage.value.trim());
            
            // Vérifier que le message n'existe pas déjà (éviter les doublons)
            const messageExists = messages.value.some(m => m.id === message.id);
            if (!messageExists) {
                messages.value.push(message);
            }
            
            // Ajouter l'ID du message à la liste des messages récemment envoyés
            // pour éviter qu'il soit ajouté à nouveau via Socket.IO
            if (message.id) {
                recentlySentMessageIds.add(message.id);
                // Retirer après 5 secondes (le temps que Socket.IO le renvoie)
                setTimeout(() => {
                    recentlySentMessageIds.delete(message.id);
                }, 5000);
            }
            
            newMessage.value = '';
            
            await nextTick();
            scrollToBottom();
        } catch (error: any) {
            errorMessage.value = error.message;
        } finally {
            isSending.value = false;
        }
    }

    async function updateStatus(newStatus: 'open' | 'closed' | 'pending') {
        if (!conversationId.value) return;
        
        try {
            loading.value = true;
            await chatService.updateStatus(conversationId.value, newStatus);
            if (conversation.value) {
                conversation.value.statut = newStatus;
            }
        } catch (error: any) {
            errorMessage.value = error.message;
        } finally {
            loading.value = false;
        }
    }

    function scrollToBottom() {
        if (messagesContainer.value) {
            messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
        }
    }

    watch(messages, () => {
        nextTick(() => scrollToBottom());
    }, { deep: true });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("main", _hoisted_1, [
    _cache[9] || (_cache[9] = _createElementVNode("div", { class: "page-space-50" }, null, -1)),
    _createElementVNode("section", _hoisted_2, [
      _cache[8] || (_cache[8] = _createElementVNode("div", { class: "h-20" }, null, -1)),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("button", {
          class: "btn white2 normal mb-4",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(router).push('/app/service-client')))
        }, [...(_cache[3] || (_cache[3] = [
          _createElementVNode("img", {
            src: "/icons/icon-arrow-left.svg",
            class: "btn-icon",
            alt: ""
          }, null, -1),
          _createElementVNode("span", null, "Retour", -1)
        ]))]),
        (conversation.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                (conversation.value.user?.avatar)
                  ? (_openBlock(), _createElementBlock("img", {
                      key: 0,
                      src: conversation.value.user.avatar,
                      class: "size-2xl rounded-100 mr-4",
                      alt: ""
                    }, null, 8, _hoisted_6))
                  : (_openBlock(), _createElementBlock("div", _hoisted_7, [
                      _createElementVNode("span", _hoisted_8, _toDisplayString(conversation.value.user?.prenom?.[0] || 'U'), 1)
                    ])),
                _createElementVNode("div", null, [
                  _createElementVNode("h2", _hoisted_9, _toDisplayString(conversation.value.user?.prenom) + " " + _toDisplayString(conversation.value.user?.nom), 1),
                  _createElementVNode("h4", _hoisted_10, _toDisplayString(conversation.value.user?.email), 1)
                ])
              ]),
              _createElementVNode("div", _hoisted_11, [
                _createElementVNode("select", {
                  class: "form-input-select",
                  value: conversation.value.statut,
                  onChange: _cache[1] || (_cache[1] = ($event: any) => (updateStatus(($event.target as HTMLSelectElement).value as 'open' | 'closed' | 'pending')))
                }, [...(_cache[4] || (_cache[4] = [
                  _createElementVNode("option", { value: "open" }, "Ouvert", -1),
                  _createElementVNode("option", { value: "closed" }, "Fermé", -1),
                  _createElementVNode("option", { value: "pending" }, "En attente", -1)
                ]))], 40, _hoisted_12),
                _cache[5] || (_cache[5] = _createElementVNode("label", null, "Statut", -1))
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_13, [
        (conversation.value && conversation.value.agent)
          ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
              _createElementVNode("img", {
                src: conversation.value.agent.avatar || '/icons/icon-user.svg',
                class: "size-2xl rounded-100 mr-4",
                alt: "Agent"
              }, null, 8, _hoisted_15),
              _createElementVNode("div", null, [
                _cache[6] || (_cache[6] = _createElementVNode("h2", { class: "font-semibold" }, "Service Client", -1)),
                _createElementVNode("h4", null, _toDisplayString(conversation.value.agent.prenom || 'Service client'), 1)
              ])
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", {
          class: "widget-chatbot-messages",
          ref_key: "messagesContainer",
          ref: messagesContainer
        }, [
          (loading.value && messages.value.length === 0)
            ? (_openBlock(), _createElementBlock("p", _hoisted_16, "Chargement des messages..."))
            : (messages.value.length === 0)
              ? (_openBlock(), _createElementBlock("p", _hoisted_17, "Aucun message dans cette conversation"))
              : (_openBlock(true), _createElementBlock(_Fragment, { key: 2 }, _renderList(messages.value, (message, index) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: message.id || index,
                    class: _normalizeClass(['message', message.qui])
                  }, _toDisplayString(message.texte), 3))
                }), 128))
        ], 512),
        _createElementVNode("div", _hoisted_18, [
          _createElementVNode("div", _hoisted_19, [
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((newMessage).value = $event)),
              type: "text",
              class: "widget-chatbot-input",
              placeholder: "Écrire un message...",
              onKeyup: _withKeys(sendMessage, ["enter"]),
              disabled: isSending.value
            }, null, 40, _hoisted_20), [
              [_vModelText, newMessage.value]
            ])
          ]),
          _createElementVNode("button", {
            class: _normalizeClass(["btn-ultra tips send", { 'loading': isSending.value }]),
            onClick: sendMessage,
            disabled: !newMessage.value.trim() || isSending.value
          }, [...(_cache[7] || (_cache[7] = [
            _createElementVNode("div", { class: "btn-show" }, [
              _createElementVNode("img", {
                src: "/icons/icon-send.svg",
                class: "btn-icon",
                alt: ""
              })
            ], -1),
            _createElementVNode("div", { class: "btn-loading" }, [
              _createElementVNode("div", { class: "loader-dots" })
            ], -1)
          ]))], 10, _hoisted_21)
        ])
      ]),
      (errorMessage.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_22, _toDisplayString(errorMessage.value), 1))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})